import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Payout = {
  propertyid: number
  payoutid: number
}
// private module logic //

const id: string = "payout"
const initialState: ApiResult<Payout[]> = { id, state: "init", value: [] }
const payoutStream = new BehaviorSubject<ApiResult<Payout[]>>(initialState)
const payoutEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let payoutMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  payoutStream.next({ id, state: 'ok', value: [] })
  payoutMap = {}
}

function get(payoutId: number): Payout {
  return payoutMap[payoutId]
}

function refresh(): Promise<any> {
  const currentValue = payoutStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchPayout(propertyid)
}


function fetchPayout(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Payout[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  payoutStream.next({ id, state: 'busy', value: payoutStream.getValue().value })

  let url = `payout?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `payout?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Payout[]) => {
    _.forEach(records, (payout) => (payoutMap[payout.payoutid] = payout))
    // merge results unique by payoutid
    payoutStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<Payout | { message: string }> {
  const currentValue = payoutStream.getValue().value
  payoutStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    inputtype: "basic",
    mosoffset: 1,
    name: "",
    payoutdetail: null
  }

  return Client.post(`payout`, payload)
    .then((payout: Payout) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, payout.payoutid),
        redofunc: () => create(propertyid)
      })
      payoutMap[payout.payoutid] = payout
      // merge results unique by payoutid
      const currentValue = payoutStream.getValue().value
      payoutStream.next({
        id,
        state: 'ok',
        value: _.unionBy([payout], currentValue, 'payoutid')
      })
      payoutEventStream.next({ type: 'PAYOUT_CREATED', payload: payout })
      return payout
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      payoutStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchPayout(propertyid)
      return err
    })
}

function copy(propertyid:number, payoutid:number, code:string) {
  const currentValue = payoutStream.getValue().value;
  payoutStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'payout', recordid: payoutid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((payout: Payout) => {
      const newRecord = payout[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.payoutid),
        redofunc: () => copy(propertyid, payoutid, code)
      });
      payoutMap[newRecord.payoutid] = newRecord
      // merge results unique by payoutid
      const currentValue = payoutStream.getValue().value
      payoutStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'payoutid').sort((a, b) => alphanum(a.code, b.code))
      })
      payoutEventStream.next({ type: 'PAYOUT_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      payoutStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchPayout(propertyid)
      return err
    })
}

function copies(propertyid: number, payoutid: number, count: number) {
  const currentValue = payoutStream.getValue().value;
  payoutStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'payout', recordid: payoutid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((payouts: Payout[]) => {
      push_undo({
        undofunc: () => _.forEach(payouts, newRecord => deleteOne(propertyid, newRecord.payoutid)),
        redofunc: () => copies(propertyid, payoutid, count)
      });
      _.forEach(payouts, newRecord => payoutMap[newRecord.payoutid] = newRecord)
      // merge results unique by payoutid
      const currentValue = payoutStream.getValue().value
      payoutStream.next({
        id,
        state: 'ok',
        value: _.unionBy(payouts, currentValue, 'payoutid').sort((a, b) => alphanum(a.code, b.code))
      })
      payoutEventStream.next({ type: 'PAYOUT_COPIED', payload: payouts })
      return payouts
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      payoutStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchPayout(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  payoutid: number,
): Promise<boolean | { message: string }> {
  const currentValue = payoutStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, payoutid)
  const refreshFn = () => fetchPayout(propertyid)
  if (!(await handleReferences('payout', payoutid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted payoutid
  const value = _(currentValue)
    .filter((p) => p.payoutid !== payoutid)
    .value()
  value.forEach((p) => (payoutMap[p.payoutid] = p))

  payoutStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(payoutid).then(() =>
  Client.del(`payout?payoutid=eq.${payoutid}`)
    .then((payout: Payout) => {
      delete payoutMap[payout.payoutid]
      push_undo({
        undofunc: () => {
          Client.post('payout', payout)
          payoutMap[payout.payoutid] = payout
          payoutStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, payoutid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      payoutStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchPayout(propertyid)
      return err
    })
  )
}

function patch(payout: Payout): Promise<Payout | { message: string }> {
  const currentValue = payoutStream.getValue().value
  const payoutid: number = payout.payoutid
  const currentpayout = _.find(currentValue, { payoutid })
  const changeset = diff_obj(payout, currentpayout)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentpayout),
    redofunc: () => patch(payout)
  })

  // optimistic update
  payoutStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {payoutid}), payout)
  })

  return _patches_.watch(payoutid, Client.patch(`payout?payoutid=eq.${payoutid}`, changeset)
    .then((mf: Payout[]) => {
      _.forEach(mf, (payout) => (payoutMap[payout.payoutid] = payout))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      payoutStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentpayout, 'propertyid')
      if (propertyid) fetchPayout(propertyid)
      return err
    }))

}

// public module interface //

const payoutService = {
  stream: () => payoutStream.asObservable(),
  value: () => payoutStream.getValue().value,
  eventStream: () => payoutEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchPayout,
  patch,
  refresh
}

export default payoutService
